import { Suspense, lazy } from "react";
import { BrowserRouter, Navigate, Route, Routes, useSearchParams } from "react-router-dom";

import { ApiProvider } from "./api/ApiProvider";
import { AuthProvider } from "./auth/AuthProvider";
import { PageLoading } from "./components/PageLoading";
import { RuhendGate } from "./components/RuhendGate";
import { AppLayout } from "./layout/AppLayout";

// Code-Splitting je Route: jede Seite wird erst beim ersten Aufruf geladen.
const AgentConfigPage = lazy(() =>
  import("./features/agent/AgentConfigPage").then((m) => ({ default: m.AgentConfigPage })),
);
const AgentTestsPage = lazy(() =>
  import("./features/agent-tests/AgentTestsPage").then((m) => ({ default: m.AgentTestsPage })),
);
const AnalyticsPage = lazy(() =>
  import("./features/analytics/AnalyticsPage").then((m) => ({ default: m.AnalyticsPage })),
);
const CallsArchivePage = lazy(() =>
  import("./features/calls/CallsArchivePage").then((m) => ({ default: m.CallsArchivePage })),
);
const CallDetailPage = lazy(() =>
  import("./features/call-detail/CallDetailPage").then((m) => ({ default: m.CallDetailPage })),
);
const CasesPage = lazy(() =>
  import("./features/case-qualification/CasesPage").then((m) => ({ default: m.CasesPage })),
);
const CompliancePage = lazy(() =>
  import("./features/compliance/CompliancePage").then((m) => ({ default: m.CompliancePage })),
);
const ConnectionsPage = lazy(() =>
  import("./features/connections/ConnectionsPage").then((m) => ({ default: m.ConnectionsPage })),
);
const ArchitekturPage = lazy(() =>
  import("./features/demo/ArchitekturPage").then((m) => ({ default: m.ArchitekturPage })),
);
const DemoPage = lazy(() =>
  import("./features/demo/DemoPage").then((m) => ({ default: m.DemoPage })),
);
const LatenzPage = lazy(() =>
  import("./features/demo/LatenzPage").then((m) => ({ default: m.LatenzPage })),
);
const ProduktdatenPage = lazy(() =>
  import("./features/demo/ProduktdatenPage").then((m) => ({ default: m.ProduktdatenPage })),
);
const SicherheitPage = lazy(() =>
  import("./features/demo/SicherheitPage").then((m) => ({ default: m.SicherheitPage })),
);
const KnowledgeBasePage = lazy(() =>
  import("./features/knowledge-base/KnowledgeBasePage").then((m) => ({ default: m.KnowledgeBasePage })),
);
const KnowledgeGapsPage = lazy(() =>
  import("./features/knowledge-gaps/KnowledgeGapsPage").then((m) => ({ default: m.KnowledgeGapsPage })),
);
const KnowledgePublicationsPage = lazy(() =>
  import("./features/knowledge-publications/KnowledgePublicationsPage").then((m) => ({ default: m.KnowledgePublicationsPage })),
);
const GradesPage = lazy(() =>
  import("./features/grades/GradesPage").then((m) => ({ default: m.GradesPage })),
);
const HelpPage = lazy(() =>
  import("./features/help/HelpPage").then((m) => ({ default: m.HelpPage })),
);
const LiveGridPage = lazy(() =>
  import("./features/live-grid/LiveGridPage").then((m) => ({ default: m.LiveGridPage })),
);
const MachineCredentialsPage = lazy(() =>
  import("./features/machine-credentials/MachineCredentialsPage").then((m) => ({ default: m.MachineCredentialsPage })),
);
const OnboardingWizard = lazy(() =>
  import("./features/onboarding/OnboardingWizard").then((m) => ({ default: m.OnboardingWizard })),
);
const OverviewPage = lazy(() =>
  import("./features/overview/OverviewPage").then((m) => ({ default: m.OverviewPage })),
);
const PlaygroundPage = lazy(() =>
  import("./features/playground/PlaygroundPage").then((m) => ({ default: m.PlaygroundPage })),
);
const PimPage = lazy(() =>
  import("./features/pim/PimPage").then((m) => ({ default: m.PimPage })),
);
const PolicyPage = lazy(() =>
  import("./features/policy/PolicyPage").then((m) => ({ default: m.PolicyPage })),
);
const AuswertungPage = lazy(() =>
  import("./features/auswertung/AuswertungPage").then((m) => ({ default: m.AuswertungPage })),
);
const AuswertungDetailPage = lazy(() =>
  import("./features/auswertung/AuswertungDetailPage").then((m) => ({ default: m.AuswertungDetailPage })),
);
const HeutePage = lazy(() =>
  import("./features/heute/HeutePage").then((m) => ({ default: m.HeutePage })),
);
const AnrufregelnPage = lazy(() =>
  import("./features/anrufregeln/AnrufregelnPage").then((m) => ({ default: m.AnrufregelnPage })),
);
const BetriebPage = lazy(() =>
  import("./features/betrieb/BetriebPage").then((m) => ({ default: m.BetriebPage })),
);
const RueckrufLandingPage = lazy(() =>
  import("./features/rueckruf").then((m) => ({ default: m.RueckrufLandingPage })),
);
const RueckrufListePage = lazy(() =>
  import("./features/rueckruf").then((m) => ({ default: m.RueckrufListePage })),
);
const SettingsPage = lazy(() =>
  import("./features/settings/SettingsPage").then((m) => ({ default: m.SettingsPage })),
);
const SummariesPage = lazy(() =>
  import("./features/summaries/SummariesPage").then((m) => ({ default: m.SummariesPage })),
);
const SummaryDetailPage = lazy(() =>
  import("./features/summaries/SummaryDetailPage").then((m) => ({ default: m.SummaryDetailPage })),
);
const TopicsPage = lazy(() =>
  import("./features/topics/TopicsPage").then((m) => ({ default: m.TopicsPage })),
);
const WorkflowsPage = lazy(() =>
  import("./features/workflows/WorkflowsPage").then((m) => ({ default: m.WorkflowsPage })),
);

/** Alte Detail-Links (/anruf-detail?id=...) landen auf dem neuen Detail, mit ID. */
export function LegacyCallDetailRedirect() {
  const [params] = useSearchParams();
  const id = params.get("id") ?? params.get("callId") ?? params.get("call_id");
  return <Navigate to={id ? `/anrufe/${encodeURIComponent(id)}` : "/anrufe"} replace />;
}

/** Alte Rückruflinks aus Mails: /warteschlange?rueckruf=<id> öffnet den Rückruf, sonst die Liste. */
export function QueueRedirect() {
  const [params] = useSearchParams();
  const id = params.get("rueckruf");
  return <Navigate to={id ? `/rueckruf/${encodeURIComponent(id)}` : "/rueckrufe"} replace />;
}

export function App() {
  return (
    <AuthProvider>
      <ApiProvider>
        <BrowserRouter>
          <Suspense fallback={<PageLoading />}>
          <Routes>
            {/* Standalone demo pages -- rendered WITHOUT AppLayout chrome */}
            <Route path="/demo" element={<DemoPage />} />
            <Route path="/demo/architektur"  element={<ArchitekturPage />} />
            <Route path="/demo/sicherheit"   element={<SicherheitPage />} />
            <Route path="/demo/produktdaten" element={<ProduktdatenPage />} />
            <Route path="/demo/latenz"       element={<LatenzPage />} />

            {/* All other routes inside the operator console layout */}
            <Route
              path="*"
              element={
                <AppLayout>
                  <Routes>
                    <Route path="/" element={<HeutePage />} />
                    <Route path="/rueckrufe" element={<RueckrufListePage />} />
                    <Route path="/rueckruf/:id" element={<RueckrufLandingPage />} />
                    <Route path="/warteschlange" element={<QueueRedirect />} />
                    <Route path="/zusammenfassungen" element={<SummariesPage />} />
                    <Route
                      path="/zusammenfassungen/:summaryId"
                      element={<SummaryDetailPage />}
                    />
                    <Route path="/auswertung" element={<AuswertungPage />} />
                    <Route path="/auswertung/:runId" element={<AuswertungDetailPage />} />
                    <Route path="/wissensluecken" element={<KnowledgeGapsPage />} />
                    <Route path="/wissensfreigaben" element={<KnowledgePublicationsPage />} />
                    <Route path="/betrieb" element={<BetriebPage />} />
                    <Route path="/betrieb/anrufregeln" element={<AnrufregelnPage />} />
                    <Route path="/verbindungen" element={<ConnectionsPage />} />

                    {/* Experte */}
                    <Route path="/wissensbasis" element={<KnowledgeBasePage />} />
                    <Route path="/pim" element={<PimPage />} />
                    <Route path="/richtlinien" element={<PolicyPage />} />
                    <Route path="/compliance" element={<CompliancePage />} />
                    <Route path="/einstellungen" element={<SettingsPage />} />
                    <Route
                      path="/einstellungen/maschinenzugaenge"
                      element={<MachineCredentialsPage />}
                    />
                    <Route path="/hilfe" element={<HelpPage />} />

                    {/* Eigene Sprachstrecke, ruht seit 23.09.2026 */}
                    <Route
                      path="/live"
                      element={<RuhendGate key="Live" title="Live"><LiveGridPage /></RuhendGate>}
                    />
                    <Route path="/anrufe" element={<CallsArchivePage />} />
                    <Route path="/anrufe/:callId" element={<CallDetailPage />} />
                    <Route path="/anruf-detail" element={<LegacyCallDetailRedirect />} />
                    <Route path="/faelle" element={<CasesPage />} />
                    <Route path="/faelle/:callId" element={<CasesPage />} />
                    <Route
                      path="/qualitaet"
                      element={<RuhendGate key="Antwortqualität" title="Antwortqualität"><GradesPage /></RuhendGate>}
                    />
                    <Route path="/themen" element={<TopicsPage />} />
                    <Route path="/agent" element={<AgentConfigPage />} />
                    <Route path="/testkonsole" element={<PlaygroundPage />} />
                    <Route
                      path="/tests"
                      element={<RuhendGate key="Tests" title="Tests"><AgentTestsPage /></RuhendGate>}
                    />
                    <Route path="/workflows" element={<WorkflowsPage />} />
                    <Route path="/einrichtung" element={<OnboardingWizard />} />
                    <Route path="/analytik" element={<AnalyticsPage />} />
                    <Route path="/uebersicht" element={<OverviewPage />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </AppLayout>
              }
            />
          </Routes>
          </Suspense>
        </BrowserRouter>
      </ApiProvider>
    </AuthProvider>
  );
}
